import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/fitness-trainer-real.jpg";

export const fitnessTrainers: Persona = {
  slug: "fitness-personal-trainers",
  profession: { hu: "edző", en: "trainer" },
  professionPlural: { hu: "edzők", en: "trainers" },
  professionGenitive: { hu: "edzőként", en: "as a trainer" },
  heroH1: {
    hu: "Az AI edzői asszisztens, ami felépíti a vállalkozásod.",
    en: "The AI training assistant that builds your business.",
  },
  heroSub: {
    hu: "Edzéstervek, csoportos órák, bérletek, online követés: egy platformon edzőknek és fitness termeknek. 10 perc alatt élő oldal, vagy mi beüzemeljük 48 órán belül.",
    en: "Training plans, group classes, passes, online tracking: one platform for trainers and gyms. Live page in 10 minutes, or we set it up within 48 hours.",
  },
  heroImage,
  heroImageAlt: "Personal trainer with client in the gym",
  chatExamplePrompt: {
    hu: "Hozz létre egy 8 hetes átalakulás programot, 65 000 Ft",
    en: "Create an 8-week transformation program, 65,000 HUF",
  },
  chatExampleResponse: {
    hu: "Létrehoztam, publikálva. Készítsek hozzá Instagram posztot?",
    en: "Created and published. Want me to draft an Instagram post too?",
  },
  accentColor: "emerald",

  defaultMode: "team",
  teamSize: { min: 1, max: 25 },
  soloLabel: { hu: "Egyéni edző", en: "Solo trainer" },
  teamLabel: {
    hu: "Fitness terem több edzővel",
    en: "Gym with multiple trainers",
  },

  soloDayInLife: [
    {
      time: "05:30",
      what: {
        hu: "Korai edzés Mártival. Az AI emlékeztette tegnap este.",
        en: "Early session with Márti. AI reminded her last night.",
      },
    },
    {
      time: "09:00",
      what: {
        hu: "Két új foglalás érkezett a weboldalról. A rendszer kezelt mindent a háttérben.",
        en: "Two new bookings came in from the website. The system handled everything in the background.",
      },
    },
    {
      time: "12:00",
      what: {
        hu: "Egy ügyfél fizetne készpénzben. Te kiállítod a számlát egy klikkre.",
        en: "A client wants to pay in cash. You issue the invoice in one click.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI észrevette, hogy Kati 3 hete nem volt. Email ment ki neki 15% kedvezménnyel.",
        en: "AI noticed Kati hasn't come in 3 weeks. Email went out with 15% off.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Új edzéstervet töltesz fel PDF-ben. Holnap reggeltől eladható a weboldalon.",
        en: "You upload a new training plan PDF. It's on sale on the site by tomorrow morning.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "07:00",
      what: {
        hu: "Megnyitod a terem dashboardját: 6 edződ 47 órát ad ma. Mindenki naptára átláthatóan.",
        en: "Open the gym dashboard: your 6 trainers run 47 sessions today. All calendars at a glance.",
      },
    },
    {
      time: "09:30",
      what: {
        hu: "Új tag regisztrált online. A rendszer kiosztotta a kiválasztott edzőhöz és kiküldte a beüzemelő emailt.",
        en: "New member registered online. System assigned them to their chosen trainer and sent the onboarding email.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy edző beteg lett. Egy klikk, az AI áthelyezi 8 ügyfelét másik edzőhöz, mindenkit értesít.",
        en: "A trainer got sick. One click and AI moves 8 clients to another trainer and notifies everyone.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Az AI mutatja: Péter edző 18%-kal több ügyfelet hoz mint az átlag. Megmutatod a többieknek a stílusát.",
        en: "AI shows: trainer Péter brings in 18% more clients than average. You share his approach with the team.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Hónap vége: automatikus elszámolás edzőnként, bér-számítás kész, számlák kiállítva.",
        en: "End of month: automatic settlement per trainer, payroll ready, invoices issued.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Marketing AI: a leggyengébb hétvégi időszakot betöltötte egy 'fél áras péntek esti' kampány.",
        en: "Marketing AI: your weakest weekend slot got filled by a 'half-price Friday night' campaign.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, ügyfél, bérlet, számla: egyetlen rendszerben. Nincs Excel, nincs WhatsApp scroll.",
        en: "Calendar, clients, passes, invoices: one system. No Excel, no WhatsApp scrolling.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Az AI a marketingedet is intézi",
        en: "AI handles your marketing too",
      },
      body: {
        hu: "Heti Instagram posztok, vissza-hívó email kampányok az inaktív ügyfeleknek.",
        en: "Weekly Instagram posts, win-back email campaigns for inactive clients.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Bérletek és online fizetés",
        en: "Passes and online payment",
      },
      body: {
        hu: "10 alkalmas, havi korlátlan, VIP: automata levonás foglaláskor.",
        en: "10-class, monthly unlimited, VIP: auto-deducted at booking.",
      },
    },
    {
      icon: "Smile",
      title: { hu: "Mobilra optimalizált", en: "Mobile-optimized" },
      body: {
        hu: "Az ügyfeleid 80%-a mobilon foglal. A Bespire kifejezetten erre épült.",
        en: "80% of your clients book on mobile. Bespire is built for that.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "1 vagy 25 edző: egy rendszer",
        en: "1 or 25 trainers: one system",
      },
      body: {
        hu: "Mindenki csak azt látja, amihez joga van, te pedig tulajdonosként mindent látsz. Edzői naptárak, ügyfélkörök, statisztikák.",
        en: "Each sees only what they need, you see it all. Trainer calendars, client books, stats.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a foglalásokat", en: "AI assigns bookings" },
      body: {
        hu: "Új ügyfél jön: az AI a szolgáltatás, hely, idő alapján a megfelelő edzőhöz osztja.",
        en: "New client comes in: AI assigns them to the right trainer based on service, location, time.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Edzői teljesítmény-statisztika",
        en: "Trainer performance stats",
      },
      body: {
        hu: "Ki mennyit dolgozott, ki hozott mennyi bevételt, melyik szolgáltatás a legnépszerűbb.",
        en: "Who worked how much, who brought in what, which service is most popular.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Számlázás központilag vagy edzőnként",
        en: "Central or per-trainer invoicing",
      },
      body: {
        hu: "Alvállalkozói modell vagy csapat-modell. Számlázz.hu / Billingo integráció.",
        en: "Contractor model or team model. Számlázz.hu / Billingo integration.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: {
        hu: "Csoportos órák és bootcampek",
        en: "Group classes and bootcamps",
      },
      body: {
        hu: "Létszámkorlát, várólista, bérletvonás, csere: minden automata.",
        en: "Capacity, waitlist, pass deduction, swaps: all automated.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Bérletek és fizetés", en: "Passes and payment" },
      body: {
        hu: "Online vagy helyszíni fizetés, automatikus számla, ajándékutalvány.",
        en: "Online or in-person payment, automatic invoicing, gift cards.",
      },
    },
    {
      icon: "Users",
      title: { hu: "Csapatkezelés", en: "Team management" },
      body: {
        hu: "1 vagy 25 edző: egy rendszer. Mindenki csak azt látja, amihez joga van, te pedig mindent látsz.",
        en: "1 or 25 trainers: one system. Each sees only what they need, you see all.",
      },
    },
    {
      icon: "TrendingUp",
      title: { hu: "Ügyfél-visszahívó kampány", en: "Win-back campaigns" },
      body: {
        hu: "Ha egy ügyfél kiesik a ritmusból, beállíthatsz automata emlékeztető emailt vagy akciós kuponos kampányt: egy kattintással elindítod.",
        en: "If a client drops off, set up an automatic reminder email or discount campaign: launch it in one click.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Több edző dolgozik nálunk: bonyolult lesz?",
        en: "We have multiple trainers: will it be complicated?",
      },
      answer: {
        hu: "Nem. Mindenki csak azt látja, amihez joga van, te tulajdonosként a központi dashboardon mindent látsz. Saját naptárak, ügyféllisták és statisztikák. Korlátlan edzőt vehetsz fel, NEM fizetsz többet érte. 1 vagy 25 fő: ugyanaz a 10 000 Ft + ÁFA / hó.",
        en: "No. Each trainer sees only their data, you as owner see everything on the central dashboard. Own calendars, client lists, stats. Add unlimited trainers, you do NOT pay more. 1 or 25 people, same 10,000 HUF + VAT / month.",
      },
    },
    {
      question: {
        hu: "Mi van, ha az ügyfél nem jelenik meg?",
        en: "What if a client doesn't show up?",
      },
      answer: {
        hu: "Beállíthatsz no-show díjat (pl. 50% levonás a bérletből), vagy automata értesítést 24 órával előtte. Ha az ügyfél megerősíti, jó. Ha nem, az AI rákérdez.",
        en: "Set a no-show fee (e.g. 50% pass deduction) or auto-notify 24 hours ahead. If they confirm, great. If not, AI follows up.",
      },
    },
    {
      question: {
        hu: "A bérleteket hogyan kezelem?",
        en: "How do I handle passes?",
      },
      answer: {
        hu: "Tetszőleges bérlet: 10 alkalmas, havi korlátlan, VIP éves. Foglaláskor automatikusan levonódik. Lejárat előtt az AI emlékeztetőt küld, hogy meghosszabbítsa.",
        en: "Any kind of pass: 10-class, monthly unlimited, VIP annual. Auto-deducted at booking. AI reminds clients to renew before expiry.",
      },
    },
    {
      question: {
        hu: "Hogy kapom meg a régi rendszerünkből az adatokat?",
        en: "How do I get data out of our old system?",
      },
      answer: {
        hu: "CSV vagy Excel importálás. Ha jelenleg MindBody, GoTeamUp vagy egyedi rendszerben vagy, a Beüzemelés szolgáltatás keretében átvesszük a teljes ügyfélbázist és bérletállományt.",
        en: "CSV or Excel import. If you're on MindBody, GoTeamUp or a custom system, our Setup service migrates the full client base and pass balances for you.",
      },
    },
  ],

  monetization: [
    {
      icon: "Dumbbell",
      title: { hu: "Edzések", en: "Training sessions" },
      examples: [
        { hu: "Személyi edzés", en: "Personal training" },
        { hu: "Csoportos funkcionális", en: "Group functional" },
        { hu: "Online edzés videóhívással", en: "Online video sessions" },
      ],
      priceHint: {
        hu: "Példa: 80 edzés × 12 000 Ft = 960 000 Ft / hó",
        en: "Example: 80 sessions × 12,000 HUF = 960,000 HUF / month",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Edzéstervek", en: "Training plans" },
      examples: [
        { hu: "8 hetes átalakulás", en: "8-week transformation" },
        { hu: "Versenyre felkészítő", en: "Competition prep" },
        { hu: "Rehabilitációs program", en: "Rehab program" },
      ],
      priceHint: {
        hu: "Példa: 15 terv × 35 000 Ft = 525 000 Ft / hó",
        en: "Example: 15 plans × 35,000 HUF = 525,000 HUF / month",
      },
    },
    {
      icon: "Users",
      title: { hu: "Csoportos programok", en: "Group programs" },
      examples: [
        { hu: "Bootcamp hétvége", en: "Bootcamp weekend" },
        { hu: "Fitness tábor", en: "Fitness camp" },
        { hu: "Reggeli edzések", en: "Morning sessions" },
      ],
      priceHint: {
        hu: "Példa: 1 tábor × 20 fő × 38 000 Ft = 760 000 Ft",
        en: "Example: 1 camp × 20 people × 38,000 HUF = 760,000 HUF",
      },
    },
    {
      icon: "Video",
      title: { hu: "Digitális termékek", en: "Digital products" },
      examples: [
        { hu: "Otthoni edzés PDF", en: "Home workout PDF" },
        { hu: "Edzésvideó csomag", en: "Workout video bundle" },
        { hu: "Táplálkozási útmutató", en: "Nutrition guide" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~150 000 Ft / hó",
        en: "Passive revenue: ~150,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "10 alkalmas", en: "10-class" },
        { hu: "Havi korlátlan", en: "Monthly unlimited" },
        { hu: "VIP éves", en: "VIP annual" },
      ],
      priceHint: {
        hu: "Csapat-szint: 6 edző × 40 havi tag × 25 000 Ft = 6 000 000 Ft / hó potenciál",
        en: "Team level: 6 trainers × 40 monthly members × 25,000 HUF = 6,000,000 HUF / month potential",
      },
    },
  ],

  results: [
    { value: "+38%", label: { hu: "bevétel / edző", en: "revenue / trainer" } },
    { value: "-71%", label: { hu: "elmaradt edzés", en: "missed sessions" } },
    {
      value: "3.2x",
      label: { hu: "több online foglalás", en: "more online bookings" },
    },
    { value: "3 nap", label: { hu: "ingyenes próba", en: "free trial" } },
  ],
  testimonial: {
    quote: {
      hu: "6 edzővel dolgozunk és korábban Excelben követtük a bérleteket. A Bespire-rel mindenki saját naptárral, és a hónap végi bérszámítás 4 óra helyett 5 perc lett.",
      en: "We work with 6 trainers and used to track passes in Excel. With Bespire everyone has their own calendar, and end-of-month payroll went from 4 hours to 5 minutes.",
    },
    name: "Németh Bálint",
    role: { hu: "Fitness terem tulajdonos", en: "Gym owner" },
    city: { hu: "Debrecen", en: "Debrecen" },
    avatarInitials: "NB",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a szolgáltatásaid",
        en: "Tell us your services in chat",
      },
      body: {
        hu: "Személyi edzés, csoportos óra, online program, tábor: az AI strukturálja.",
        en: "Personal training, group classes, online programs, camps: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI felépíti a teljes rendszert",
        en: "AI builds the full system",
      },
      body: {
        hu: "Edző-profilok, bérletek, csoportos órák, várólista: élesítve.",
        en: "Trainer profiles, passes, group classes, waitlists: live.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Migráljuk a meglévő ügyfelek + bérleteket",
        en: "We migrate existing clients + passes",
      },
      body: {
        hu: "MindBody, GoTeamUp, Excel: bárhonnan, ingyen, 30 perc.",
        en: "MindBody, GoTeamUp, Excel: from anywhere, free, 30 minutes.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő weboldal és foglalás",
        en: "Live website and bookings",
      },
      body: {
        hu: "Online foglalás 24/7, automata emlékeztetők, kampányok az inaktív ügyfeleknek.",
        en: "24/7 online booking, auto-reminders, win-back campaigns for inactive clients.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Google Meet",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: {
    hu: "Fitness és személyi edzők",
    en: "Fitness & personal trainers",
  },
  similarPersonas: ["yoga-pilates-instructors", "sport-clubs", "nutritionists"],
};
